import { CommonModule } from "@angular/common";
import { Component, ContentChild, EventEmitter, inject, Output, signal } from "@angular/core";
import { Router } from "@angular/router";
import { ConfirmSignUpComponent } from "./components/pages/confirm-sign-up/confirm-sign-up.component";
import { ResetPasswordComponent } from "./components/pages/reset-password/reset-password.component";
import { SignInAndSignUpComponent } from "./components/pages/sign-in-and-sign-up/sign-in-and-sign-up.component";
import { AuthContentDirective } from "./directives/auth-content/auth-content.directive";
import { AuthenticateStateEnum } from "./enums/authenticate.enum";
import { AuthService } from "./services/auth.service";

@Component({
  selector: "keus-auth",
  imports: [
    CommonModule,
    SignInAndSignUpComponent,
    ConfirmSignUpComponent,
    ResetPasswordComponent,
  ],
  templateUrl: "./auth.component.html",
  styleUrl: "./auth.component.css",
})
export class AuthComponent {
  /** Injected AuthService instance */
  private readonly _authService: AuthService = inject(AuthService);

  /** Injected the Router */
  private readonly _router: Router = inject(Router);

  /** Enum for authentication states */
  public readonly authenticateState: typeof AuthenticateStateEnum =
    AuthenticateStateEnum;

  /** Observable for authentication state */
  public readonly isAuthenticated$ = this._authService.isAuthenticated$();

  /** Content child directive for custom authentication content */
  @ContentChild(AuthContentDirective)
  public authContent?: AuthContentDirective;

  /** Signal for authentication state */
  public readonly state = signal(AuthenticateStateEnum.SIGN_IN_AND_SIGN_UP);

  /** Event emitted when authentication is successfully completed */
  @Output() public readonly authenticationCompleted = new EventEmitter<void>();

  /**
   * Changes the authentication state.
   * @param event - The new authentication state.
   */
  public changeAuthState(event: AuthenticateStateEnum): void {
    this.state.set(event);

    // Check authentication status and emit completion event if authenticated
    this._authService.isAuthenticated$().subscribe(isAuthenticated => {
      if (isAuthenticated) {
        // Emit event instead of navigating directly
        this.authenticationCompleted.emit();
      }
      // No manual navigation - let the parent component handle the flow
    }).unsubscribe(); // Unsubscribe immediately since we only need current value
  }
}
